import React, { useState, useEffect, useMemo, useContext } from 'react';

import { OneOnOneInstanceContext as DeerOneOnOneInstanceContext } from 'Deer/pages/MyData/OneOnOne/LegacyInstancePage/LegacyInstancePage';
import { disableAgendaEditingAction, enableAgendaEditingAction, removeAgendaAction } from 'actions/agenda';
import { StyledAvatar } from 'components/avatars';
import PostField from 'components/feed/PostField';
import TextParseComponent from 'components/feed/TextParseComponent';
import { useSelector } from 'react-redux';
import { OneOnOneInstanceContext } from 'screens/oneOnOne/instancePage/InstancePageContext';
import useAgendaSidePanelContext from 'screens/oneOnOne/levelUp/agendaSidePanel/context';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import styled, { css } from 'styled-components';
import {
  StyledTypography,
  editorStyles,
  editorPlaceholderStyles,
  StrikethroughCSS1,
  FlexContainer,
  IconSizesCSS,
} from 'styles/common';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { CREATE_TASK_PLACEHOLDER, SEARCH_WIDGET_TYPES } from 'constants/ProjectConstants';
import { UncheckedCircle } from 'constants/icons';
import { sendCompletedOneononeAgenda } from 'constants/mixpanelHelper';
import theme from 'constants/theme';

import { convertTextMacrosToEditorState } from 'services/helpers/post';

import CircularOutlineIcon from 'uiToolkit/CircularOutlineIcon';

import { StyledNodesView, SubTaskCheck } from '../sidepanelStyles';
import AgendaMenuOptions from './AgendaMenuOptions';
import TaggedAgenda from './TaggedAgenda';
import { RightArrowIcon } from './commonComponents';
import { getCompletedColor } from './helper';

const whiteBackgroundInputStyles = css`
  border: 1px solid #eee;
  border-radius: 4px;
  padding: calc(0.25rem - 1px) calc(0.5rem - 1px);
  background: #ffffffdb;
`;

const currentPostFieldPlaceholderStyles = css`
  ${editorPlaceholderStyles};
  top: 0.25rem;
  left: 0.5rem;
  font-size: ${(props) => props.theme.typography.body2.fontSize};
`;

const whiteBackgroundEditorStyles = css`
  ${editorStyles};
  ${whiteBackgroundInputStyles};
  font-size: ${(props) => props.theme.typography.body2.fontSize};
  line-height: ${(props) => props.theme.typography.body2.lineHeight};
`;

const AgendaDisplay = function ({
  agendasDispatch,
  agenda,
  onCheckToggle,
  onDelete,
  onUpdate,
  teamId,
  filterEmpIds = null,
  isPlugin = false,
}) {
  const { text, macros } = agenda;
  const contextToUse = checkNewNavEnabled() ? DeerOneOnOneInstanceContext : OneOnOneInstanceContext;

  const { openAgendaSidePanel } = useAgendaSidePanelContext();
  const { userRole } = useSelector(selectLoggedInEmployeeData);

  const {
    state: { instanceDetails },
  } = useContext(contextToUse);

  const checkInInstanceId = instanceDetails?.id;
  const checkInParentId = instanceDetails?.parentTaskId;

  const [currentEditorState, setCurrentEditorState] = useState(() =>
    convertTextMacrosToEditorState(agenda.text, agenda.macros)
  );

  const currentOwners = useMemo(
    () => [
      {
        ...agenda.fromEmployee,
        image: agenda.fromEmployee.profilePhotoSrc,
        fullName: agenda.fromEmployee.displayName,
      },
    ],
    [agenda.fromEmployee]
  );

  useEffect(() => {
    if (text) setCurrentEditorState(convertTextMacrosToEditorState(agenda.text, agenda.macros));
  }, [text, macros]);

  const handleUpdate = () => {
    onUpdate({
      ...agenda,
      text: currentEditorState.getCurrentContent().getPlainText().trim(),
      isEditing: false,
    });
  };

  const handleClick = (ev) => {
    ev.stopPropagation();

    if (!agenda?.uuid) return;

    openAgendaSidePanel({
      agendaId: agenda.uuid,
      agendaText: agenda.text,
      checkInParentId,
      checkInInstanceId,
      filterEmpIds,
    });
    ev.stopPropagation();
  };

  const handleCancel = () => {
    setCurrentEditorState(convertTextMacrosToEditorState(agenda.text, agenda.macros));
    const action = agenda.uuid
      ? disableAgendaEditingAction.bind(null, { agendaId: agenda.uuid, key: agenda.uiKey })
      : removeAgendaAction.bind(null, agenda.uuid, agenda.uiKey);
    agendasDispatch(action());
  };

  if (agenda.label) {
    return (
      <TaggedAgenda
        agendasDispatch={agendasDispatch}
        agenda={agenda}
        onCheckToggle={onCheckToggle}
        onDelete={onDelete}
        isPlugin={isPlugin}
        filterEmpIds={filterEmpIds}
      />
    );
  }

  return (
    <Container isEditing={agenda.isEditing} onClick={handleClick}>
      <LeftChildContainer>
        <SubTaskCheck
          onClick={(e) => {
            e.stopPropagation();
            onCheckToggle(agenda);
            if (agenda.status !== 'COMPLETED') {
              sendCompletedOneononeAgenda({
                eventProperties: {
                  role: userRole,
                  type: 'None',
                },
              });
            }
          }}
          disableRipple
          checked={agenda.status === 'COMPLETED'}
          checkedIcon={<UncheckedCircle size="smaller" color={theme.palette.icons.blue} />}
          size="h6"
          icon={<UncheckedCircle size="smaller" color="textSecondary" />}
          style={{ padding: '0 1rem 0 0' }}
        />
        {agenda.isEditing ? (
          <StyledPostField
            placeholder={CREATE_TASK_PLACEHOLDER}
            editorState={currentEditorState}
            updateEditorState={setCurrentEditorState}
            // teamId={teamId}
            editorStyles={whiteBackgroundEditorStyles}
            placeholderStyles={currentPostFieldPlaceholderStyles}
            handleBlur={handleUpdate}
            onEnter={handleUpdate}
            onEscape={handleCancel}
            widgetType={SEARCH_WIDGET_TYPES.agenda}
            mentionEmpIds={filterEmpIds}
          />
        ) : (
          <div style={{ flexGrow: 1 }}>
            <TaskNameTypography
              variant="body2"
              display="inline"
              completed={agenda.status === 'COMPLETED'}
              textDecorationColor={getCompletedColor.call(agenda, 'textPrimary')}
              onClick={(ev) => {
                ev.stopPropagation();
                agendasDispatch(enableAgendaEditingAction({ agendaId: agenda.uuid, key: agenda.uiKey }));
              }}
            >
              <TextParseComponent text={agenda.text} res={agenda.macros} />
            </TaskNameTypography>
          </div>
        )}
      </LeftChildContainer>
      <RightChildContainer>
        <StyledNodesView
          height="100%"
          useEmployeeClick={false}
          onClick={(e) => {
            e.preventDefault();
          }}
          employees={currentOwners}
          maxNodes={2}
          tooltipPrefix="Added by "
          renderAvatar={(props) => (
            <ImageContainer rightAligned={false}>
              <StyledAvatar {...props} size="smallest" leftGutter={-0.5} alt="" />
            </ImageContainer>
          )}
        />

        {!isPlugin && agenda?.uuid && (
          <HoverOptionItems>
            <AgendaMenuOptions menuOptions={['DELETE']} agendaId={agenda?.uuid} onDelete={onDelete} />
            <StyledRightArrowIcon />
          </HoverOptionItems>
        )}

        {/* </DisplayActions> */}
      </RightChildContainer>
    </Container>
  );
};

export default AgendaDisplay;

const StyledRightArrowIcon = styled(RightArrowIcon)`
  margin-left: 16px;
`;

const ImageContainer = styled.div`
  display: flex;
  align-items: center;
  padding: ${(props) => props.padding};
  ${(props) =>
    props.alignSelf &&
    css`
      align-self: ${props.alignSelf};
    `}
`;

export const HoverOptionItems = styled(FlexContainer)`
  margin-left: 16px;
  justify-content: flex-end;
  visibility: hidden;
`;

const TaskNameTypography = styled(StyledTypography)`
  ${StrikethroughCSS1};
  padding-right: 0.5rem;
  padding-left: 0.5rem;
  // adding transparent styles to avoid shifting issue
  border: 1px solid transparent;
  border-radius: 4px;
  padding: calc(0.25rem - 1px) calc(0.5rem - 1px);
  background: transparent;
`;

const Container = styled.div`
  cursor: pointer;
  display: flex;
  margin: 0 -1rem 0 -1.5rem;
  padding: 0.5rem;
  justify-content: space-between;
  // min-height: calc(2rem + ${(props) => props.theme.typography.body2.lineHeight}rem);
  ${(props) =>
    props.isEditing &&
    css`
      background-color: ${(props) => props.theme.palette.background.lighterBlue};
    `}
  &:hover {
    // padding: 0.25rem 0 0.25rem 0;
    background-color: ${(props) => props.theme.palette.background.lighterBlue};
    ${HoverOptionItems} {
      visibility: visible;
    }
    ${TaskNameTypography} {
      display: inline-block;
      min-width: 60%;
      margin-left: 0;
      cursor: text;
      ${whiteBackgroundInputStyles};
      min-height: 60%;
      ${(props) => props.theme.breakpoints.down('xs')} {
        min-width: 100%;
      }
    }
  }
  ${(props) => props.theme.breakpoints.down('xs')} {
    margin: 0;
    padding: 16px 24px;
  }
`;

const LeftChildContainer = styled.div`
  flex: 1 1 auto;
  padding-right: 1rem;
  display: flex;
  align-items: center;
  margin-left: 1rem;
  ${(props) => props.theme.breakpoints.down('xs')} {
    margin: 0;
    flex-grow: 1;
    flex-basis: auto;
    padding-right: 24px;
  }
`;

const RightChildContainer = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-right: 1rem;
  ${(props) => props.theme.breakpoints.down('xs')} {
    margin: 0;
    height: 28px;
    width: 28px;
    flex-basis: auto;
  }
`;

const StyledPostField = styled((props) => <PostField {...props} />)`
  width: 100%;
`;

const StyledCircularOutlineIcon = styled(CircularOutlineIcon)`
  ${(props) => props.size && IconSizesCSS[props.size]};
`;
